import { useCallback, useMemo } from 'react';
import {
  SetterOrUpdater,
  atom,
  useRecoilState,
  selector,
  GetRecoilValue,
  useRecoilValue,
  atomFamily,
} from 'recoil';
import { MapBaseLayer } from 'features/mapping/mapping.types';
import { isKeyInObject } from 'shared/types';
import { localStorageEffect } from 'libs/recoil';
import {
  MapBaseLayerNames,
  MapLayer,
  MapViewDimension,
} from 'features/mapping/mapping.constants';
import { ENV } from 'shared/ENV';
import { useMapLayersStateStorageKey } from './useMapLayersStateStorageKey';
import { LOCAL_STORAGE_KEY } from '../localStorage';
import { runMapLayerDefaultMigrations } from './defaultMigrations';
import { initialMapLayersState } from './useMapLayersState.constants';

export type MapDrawerState = {
  disabledLayers: MapLayer[];
};

export type BaseMapLayer = 'Street Map' | 'Satellite Map' | 'Topographic Map';

type UseMapLayerState = {
  mapBaseLayer: BaseMapLayer;
  setMapBaseLayer: (layer: BaseMapLayer) => void;
  mapViewDimension: MapViewDimension;
  is3D: boolean;
  setMapViewDimension: (dimension: MapViewDimension) => void;
  mapLayers: MapLayer[];
  setMapLayers: SetterOrUpdater<Record<MapLayer, boolean>>;
  isDarkBaseLayer: boolean;
  isLightBaseLayer: boolean;
  setMapLayerVisibility: (name: string, visibility: boolean) => void;
  disabledLayers: MapLayer[];
  setDisabledLayers: (disabledLayers?: MapLayer[]) => void;
};

// Merge persisted map-layers value with `initialMapLayersState`.
// Fills in missing keys with current defaults (handles new layers and the
// legacy string[] format). Saved values for existing keys are preserved.
export const mergeMapLayersValue = (
  savedValue: string[] | Record<string, boolean>,
): Record<MapLayer, boolean> => {
  let parsedSavedValue: Record<string, boolean> = {};
  if (Array.isArray(savedValue)) {
    savedValue.forEach((layer) => {
      parsedSavedValue[layer] = true;
    });
  } else {
    parsedSavedValue = { ...savedValue };
  }

  return { ...initialMapLayersState, ...parsedSavedValue };
};

const initialDrawerState: MapDrawerState = {
  disabledLayers: [],
};

const mapViewDimensionAtom = atom<MapViewDimension>({
  key: LOCAL_STORAGE_KEY.MAP_VIEW_DIMENSION,
  default: MapViewDimension['2D'],
  effects: [localStorageEffect(LOCAL_STORAGE_KEY.MAP_VIEW_DIMENSION)],
});

export const mapViewDimensionSelector = selector<boolean>({
  key: 'is3D',
  get: ({ get }: { get: GetRecoilValue }) => {
    const viewMode = get(mapViewDimensionAtom);
    return viewMode === MapViewDimension['3D'];
  },
});

const mapBaseLayerAtom = atom({
  key: LOCAL_STORAGE_KEY.MAP_BASE_LAYER,
  default: MapBaseLayerNames.STREET as MapBaseLayer,
  effects: [localStorageEffect(LOCAL_STORAGE_KEY.MAP_BASE_LAYER)],
});

export const mapLayersAtom = atomFamily<Record<MapLayer, boolean>, string>({
  key: LOCAL_STORAGE_KEY.MAP_LAYERS,
  default: initialMapLayersState,
  effects: (storageKey) => [
    // Apply any pending map-layer default migrations before the atom reads localStorage.
    () => runMapLayerDefaultMigrations(),
    localStorageEffect(storageKey, mergeMapLayersValue),
  ],
});

export const mapLayersDrawerAtom = atom({
  key: LOCAL_STORAGE_KEY.MAP_LAYERS_DRAWER,
  default: { ...initialDrawerState },
});

export const useMapLayersState = (): UseMapLayerState => {
  const is3D =
    useRecoilValue(mapViewDimensionSelector) && ENV.VITE_3D_MAPS_ENABLED;
  const [mapBaseLayer, setMapBaseLayer] = useRecoilState(mapBaseLayerAtom);
  const [mapViewDimension, setMapViewDimension] =
    useRecoilState(mapViewDimensionAtom);
  const mapLayersStateStorageKey = useMapLayersStateStorageKey();
  const [mapLayers, setMapLayers] = useRecoilState(
    mapLayersAtom(mapLayersStateStorageKey),
  );
  const [mapLayersDrawer, setMapLayersDrawer] =
    useRecoilState(mapLayersDrawerAtom);

  const setDisabledLayers = useCallback(
    (disabledLayers: MapLayer[] = []) => {
      setMapLayersDrawer((currVal) => ({ ...currVal, disabledLayers }));
    },
    [setMapLayersDrawer],
  );

  const setMapLayerVisibility = useCallback(
    (layerName: string, visibility: boolean) => {
      setMapLayers((currVal) => ({ ...currVal, [layerName]: visibility }));
    },
    [setMapLayers],
  );

  const activeMapLayers = useMemo(() => {
    return Object.keys(mapLayers).reduce<MapLayer[]>((activeLayers, layer) => {
      if (isKeyInObject(layer, mapLayers) && mapLayers[layer]) {
        activeLayers.push(layer);
      }
      return activeLayers;
    }, []);
  }, [mapLayers]);

  return {
    mapBaseLayer,
    setMapBaseLayer,
    mapViewDimension,
    setMapViewDimension,
    is3D,
    mapLayers: activeMapLayers,
    setMapLayers,
    isDarkBaseLayer: mapBaseLayer === MapBaseLayerNames.SATELLITE,
    isLightBaseLayer: mapBaseLayer !== MapBaseLayerNames.SATELLITE,
    setMapLayerVisibility,
    disabledLayers: mapLayersDrawer.disabledLayers,
    setDisabledLayers,
  };
};
